// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/posture/PostureIntelligencePanel.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";
import "./PostureIntelligencePanel.css";

const POSTURE_PROMPTS = [
  "What needs attention now?",
  "Why is posture where it is?",
  "Can I trust the current posture?",
  "What should I focus on first?",
];

function normalizeCard(value) {
  return value && typeof value === "object" ? value : null;
}


function formatConfidence(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sourceCountLabel(item = {}) {
  const itemCount = Number(item.item_count);
  if (Number.isFinite(itemCount) && itemCount >= 0) return `${itemCount} represented`;

  const records = Number(item.records);
  if (Number.isFinite(records) && records >= 0) return `${records} loaded`;

  return "";
}

function PostureSkeleton() {
  return (
    <div className="posture-ai-panel__skeleton" aria-label="Loading Posture Intelligence" role="status">
      <span />
      <span />
      <span />
    </div>
  );
}

export default function PostureIntelligencePanel({
  requestJson,
  framework,
  refreshKey = 0,
}) {
  const [card, setCard] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(POSTURE_PROMPTS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

    const [detailsOpen, setDetailsOpen] = useState(false);
  const requestSequenceRef = useRef(0);
  const mountedRef = useRef(true);

  const ask = useCallback(
    async (question) => {
      if (typeof requestJson !== "function") {
        setError("Posture Intelligence is not available because the Posture API client is not configured.");
        return;
      }

      const requestSequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestSequence;
      setLoading(true);
      setError("");
      setActiveQuestion(question);

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          body: {
            scope: "posture",
            question,
            copilot_context: {
              page: "posture",
              surface: "posture_page",
              intelligence_mode: "page",
              framework,
            },
          },
        });

        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;

        const nextCard = normalizeCard(payload?.card);
        if (!nextCard) throw new Error("Posture Intelligence returned no grounded response card.");

        setCard(nextCard);
      } catch (err) {
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
        console.error("Posture ambient intelligence error:", err);
        setError(String(err?.message || err || "Unable to load Posture Intelligence."));
      } finally {
        if (mountedRef.current && requestSequence === requestSequenceRef.current) {
          setLoading(false);
        }
      }
    },
    [framework, requestJson]
  );

  useEffect(() => {
    mountedRef.current = true;
    ask(POSTURE_PROMPTS[0]);

    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, [ask, refreshKey]);

  const recommendations = Array.isArray(card?.recommended_actions) ? card.recommended_actions : [];
  const evidence = Array.isArray(card?.evidence_used) ? card.evidence_used : [];
  const limitations = Array.isArray(card?.known_limitations) ? card.known_limitations : [];
  const metadata = card?.metadata && typeof card.metadata === "object" ? card.metadata : {};
  const auditPersisted = metadata?.audit_persisted === true;
  const responseMode = String(metadata?.ai_response_mode || "").trim();

  return (
    <section
      className="posture-ai-panel"
      aria-labelledby="posture-ai-panel-title"
    >
      <div className="posture-ai-panel__header">
        <div className="posture-ai-panel__heading">
          <div className="posture-ai-panel__eyebrow">OASYS INTELLIGENCE</div>
          <h2 id="posture-ai-panel-title">Posture Intelligence</h2>
          <p>
            Grounded reasoning across the selected framework. Canonical raw posture, operational posture,
            control state, evidence freshness, accepted risk, and active findings remain authoritative.
          </p>
        </div>

        <div className="posture-ai-panel__header-meta">
          <div className="posture-ai-panel__status">
            <StatusChip
              tone={confidenceTone(card?.confidence)}
              dot={Boolean(card)}
            >
              {loading
                ? "Reasoning…"
                : card
                ? formatConfidence(card.confidence)
                : "Awaiting context"}
            </StatusChip>

            {auditPersisted === true ? (
              <StatusChip tone="success">Audited</StatusChip>
            ) : null}

            <Button
              variant="ghost"
              size="small"
              onClick={() => setDetailsOpen((current) => !current)}
              aria-expanded={detailsOpen}
              aria-label={
                detailsOpen
                  ? "Hide intelligence grounding details"
                  : "Show intelligence grounding details"
              }
              className="posture-ai-panel__trust-details"
            >
              {detailsOpen ? "Hide details" : "Details"}
            </Button>
          </div>

        <div className="posture-ai-panel__scope">
          <span>Framework</span>
          <strong>{framework || "Current"}</strong>
        </div>
        </div>
      </div>

      <div className="posture-ai-panel__prompts" role="group" aria-label="Posture Intelligence questions">
        {POSTURE_PROMPTS.map((question) => (
          <button
            key={question}
            type="button"
            className={`posture-ai-panel__prompt ${activeQuestion === question ? "is-active" : ""}`}
            onClick={() => ask(question)}
            disabled={loading}
            aria-pressed={activeQuestion === question}
          >
            {question}
          </button>
        ))}
      </div>

      <div className="posture-ai-panel__body" aria-live="polite">
        {!card && loading ? (
          <PostureSkeleton />
        ) : (
          <>
            {card ? (
              <div className="posture-ai-panel__answer">
                <div className="posture-ai-panel__answer-topline">
                  <span>Posture brief</span>
                  {loading ? <span className="posture-ai-panel__refreshing">Refreshing…</span> : null}
                </div>

                <div className="posture-ai-panel__summary">{card.summary}</div>

                {card.reasoning ? <p className="posture-ai-panel__reasoning">{card.reasoning}</p> : null}

                {recommendations.length ? (
                  <div className="posture-ai-panel__actions">
                    <div className="posture-ai-panel__section-label">Operator focus</div>
                    <div className="posture-ai-panel__action-grid">
                      {recommendations.slice(0, 4).map((item, index) => (
                        <div className="posture-ai-panel__action" key={`${String(item)}-${index}`}>
                          <span>{index + 1}</span>
                          <p>{String(item)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {error ? <div className="posture-ai-panel__error" role="alert">{error}</div> : null}
          </>
        )}
      </div>

      {detailsOpen ? (
        <div className="posture-ai-panel__details">
          <div className="posture-ai-panel__details-body">
              <div className="posture-ai-panel__audit-grid">
                <div><span>Scope</span><strong>{framework || "Current framework"}</strong></div>
                <div><span>Mode</span><strong>{responseMode || "grounded"}</strong></div>
                <div><span>Sources</span><strong>{evidence.length}</strong></div>
              </div>

              {evidence.length ? (
                <div className="posture-ai-panel__detail-section">
                  <span>Grounding</span>
                  {evidence.map((item, index) => (
                    <div className="posture-ai-panel__evidence-row" key={`${item?.source || "source"}-${index}`}>
                      <strong>{item?.label || item?.source || "Posture source"}</strong>
                      <span>{sourceCountLabel(item)}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {limitations.length ? (
                <div className="posture-ai-panel__detail-section">
                  <span>Known limitations</span>
                  {limitations.map((item, index) => <p key={`${String(item)}-${index}`}>{String(item)}</p>)}
                </div>
              ) : null}
            </div>
        </div>
      ) : null}
    </section>
  );
}
